import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Cloud, Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useI18n } from '@/lib/i18n'
import { LANGS } from '@/lib/lang'
import { cn } from '@/lib/utils'

/** Language switcher: swaps the URL prefix and persists a `lang` cookie. */
function LanguageSwitch() {
  const { lang, switchLang } = useI18n()
  return (
    <div className="flex items-center gap-0.5 rounded-full border bg-muted/50 p-0.5">
      {LANGS.map((candidate) => (
        <button
          key={candidate}
          type="button"
          onClick={() => switchLang(candidate)}
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-semibold uppercase transition-colors',
            candidate === lang
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {candidate}
        </button>
      ))}
    </div>
  )
}

/** Sticky app header with responsive mobile menu. */
export function Navbar() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const { t, path } = useI18n()

  const NAV_LINKS = [
    { key: 'navTools', href: path('/#tools'), activePrefix: '/tools' },
    { key: 'navApi', href: path('/#api'), activePrefix: null },
    { key: 'navPricing', href: path('/#pricing'), activePrefix: null },
  ] as const

  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to={path('/')} className="flex items-center gap-2" onClick={() => setOpen(false)}>
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Cloud className="h-4 w-4" aria-hidden />
          </span>
          <span className="text-lg font-bold tracking-tight">CloudTools</span>
        </Link>

        {/* Desktop navigation */}
        <div className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((link) => {
            const active = link.activePrefix !== null && location.pathname.startsWith(link.activePrefix)
            return (
              <Link
                key={link.key}
                to={link.href}
                className={cn(
                  'text-sm font-medium transition-colors hover:text-foreground',
                  active ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {t(link.key)}
              </Link>
            )
          })}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitch />
          <Button asChild variant="outline" size="sm">
            <Link to={path('/tools/video-converter')}>{t('navStart')}</Link>
          </Button>
        </div>

        {/* Mobile menu toggle */}
        <button
          type="button"
          className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-label={t('navMenu')}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="border-t bg-background px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.key}
                to={link.href}
                className="rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
                onClick={() => setOpen(false)}
              >
                {t(link.key)}
              </Link>
            ))}
            <div className="mt-1 flex items-center justify-between px-3 py-2">
              <LanguageSwitch />
              <Button asChild size="sm">
                <Link to={path('/tools/video-converter')} onClick={() => setOpen(false)}>
                  {t('navStart')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}